import {
  POPULATION_TYPE,
  BaseListItem,
  ListResult,
  PopulationTypeHelper,
} from "../models";
import { UseLoading } from "./loading";
import { ref, Ref, computed } from "@vue/composition-api";
import { UseSearch } from "./search";
import {
  getContacts,
  getEmployees,
  searchEntities,
  getFolders,
  getPersonalAddress,
  getOrganizations,
} from "@/common/api/entities.api";
import { emptyFn, isEmpty } from "@/fp";
import { ApiResult } from "../api/ApiResult.model";
import { SharedState } from "../state";
import { SearchTargetType } from "@/gql/types";

export function UseEntities(params: {
  population: POPULATION_TYPE;
  prepend?: BaseListItem[];
  afterEachLoad?: (items: BaseListItem[]) => void;
}) {
  const { population, afterEachLoad = emptyFn(), prepend = [] } = params;
  let currentPopulation = population;
  const { isLoading, setLoading } = UseLoading(false);

  const departments_list = computed(() =>
    Object.entries(SharedState.departments.value).map(([id, name]) => ({
      id,
      name,
      type: POPULATION_TYPE.DEPARTMENT,
    }))
  );

  const run_when_idle = (cb: () => void) => {
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      // @ts-ignore
      window.requestIdleCallback(cb);
    } else {
      setTimeout(cb, 0);
    }
  };

  // Cache flattened label list for search performance
  let cached_labels_for_search: BaseListItem[] | null = null;
  const get_labels_for_search = (): BaseListItem[] => {
    if (!cached_labels_for_search) {
      cached_labels_for_search = Object.values(
        SharedState.allInfoTypes.value
      ).map(
        (type: any) =>
          ({
            id: type.id,
            isParent: type.parentId === "L0",
            name: type.labelName || type.name,
            type: POPULATION_TYPE.LABEL,
            disable: false,
          } as BaseListItem)
      );
    }
    return cached_labels_for_search;
  };

  const entities: Ref<BaseListItem[]> = ref([]);
  const cursor = ref("");

  const getEntities = async (p: {
    population: POPULATION_TYPE;
    start?: number;
    query?: string;
  }): Promise<ApiResult<ListResult<BaseListItem>>> => {
    const { population, start: offset = 0 } = p;
    const pageSize = 100;
    const page = Math.ceil(offset / pageSize);
    switch (population) {
      case POPULATION_TYPE.PRIVATE_EMAIL:
        return getPersonalAddress(cursor.value);
      case POPULATION_TYPE.FILE:
        return searchEntities({
          targetType: SearchTargetType.Files,
          offset,
          searchQuery: "%20",
        });
      case POPULATION_TYPE.CONTACT:
        return getContacts(cursor.value);
      case POPULATION_TYPE.ORGANIZATION:
        return getOrganizations(cursor.value);
      case POPULATION_TYPE.LABEL:
        if (offset > 0) {
          return Promise.resolve({
            success: true,
            value: {
              cursor: "",
              objects: [],
            },
          });
        }
        return Promise.resolve({
          success: true,
          value: {
            cursor: "",
            objects: [],
          },
        });
      case POPULATION_TYPE.FOLDER:
        return getFolders(cursor.value);
      case POPULATION_TYPE.DEPARTMENT:
        return Promise.resolve({
          success: true,
          value: {
            cursor: "",
            objects: [...departments_list.value],
          },
        });
      case POPULATION_TYPE.EMPLOYEE:
      default:
        return getEmployees(cursor.value);
    }
  };

  const search = async (p: {
    population: POPULATION_TYPE;
    cursor?: string;
    query?: string;
  }): Promise<ApiResult<ListResult<BaseListItem>>> => {
    const { population, query = "", cursor = "" } = p;
    if (population === POPULATION_TYPE.LABEL) {
      // Use cached labels and defer filtering to avoid blocking UI
      return new Promise((resolve) => {
        const performSearch = () => {
          const labels = get_labels_for_search();
          const lowerQuery = query.toLowerCase();
          const filtered = labels.filter((l) =>
            l.name.toLowerCase().includes(lowerQuery)
          );
          resolve({
            success: true,
            value: {
              cursor: "",
              objects: filtered,
            },
          });
        };

        // Use run_when_idle for non-blocking search
        run_when_idle(performSearch);
      });
    } else if (population === POPULATION_TYPE.DEPARTMENT) {
      const lower_cased_q = query.toLowerCase();
      return Promise.resolve({
        success: true,
        value: {
          cursor: "",
          objects: departments_list.value.filter(
            (d) => d.name.toLowerCase().search(lower_cased_q) > -1
          ),
        },
      });
    } else {
      return searchEntities({
        searchQuery: query,
        targetType: PopulationTypeHelper.asSearchTargetType(population),
        cursor,
      });
    }
  };

  const loadItems = async () => {
    setLoading(true);
    const res = await getEntities({ population: currentPopulation });
    if (res.success) {
      cursor.value = res.value.cursor;
      if (currentPopulation === POPULATION_TYPE.FILE) {
        //@ts-ignores
        entities.value = [
          ...prepend,
          //@ts-ignores
          ...res.value.objects.filter((f) => f.clusterUID),
        ];
      } else {
        entities.value = [...prepend, ...res.value.objects];
      }
      afterEachLoad(res.value.objects);
    }
    setLoading(false);
  };

  const loadMoreItems = async () => {
    if (isLoading.value || isEmpty(cursor.value)) {
      return;
    }
    setLoading(true);
    const res = await getEntities({
      population: currentPopulation,
      start: entities.value.length,
    });
    if (res.success) {
      cursor.value = res.value.cursor;
      if (currentPopulation === POPULATION_TYPE.FILE) {
        //@ts-ignores
        entities.value = [
          ...entities.value,
          //@ts-ignores
          ...res.value.objects.filter((f) => f.clusterUID),
        ];
      } else {
        entities.value = [...entities.value, ...res.value.objects];
      }
      afterEachLoad(res.value.objects);
    }
    setLoading(false);
  };

  const {
    isSearching,
    results: searchResults,
    query,
    clearSearch,
  } = UseSearch(async (query: string, last_cursor: string) => {
    setLoading(true);
    const res = await search({
      population: currentPopulation,
      cursor: last_cursor,
      query,
    });
    setLoading(false);
    if (res.success) {
      return res.value;
    } else {
      return {
        cursor: "",
        objects: [],
      };
    }
  });

  const displayItems = computed(() =>
    isSearching.value ? searchResults.value : entities.value
  );

  const reset = () => {
    entities.value = [];
    cursor.value = "";
    setLoading(false);
    clearSearch();
  };

  const changeEntity = (p: POPULATION_TYPE) => {
    if (p === currentPopulation) {
      return;
    }
    reset();
    currentPopulation = p;
    // Clear label cache when switching entities to ensure fresh data
    cached_labels_for_search = null;
    if (currentPopulation === POPULATION_TYPE.LABEL) {
      loadItems();
    }
  };

  const info_types = computed(() => SharedState.info_types_tree.value);

  return {
    displayItems,
    query,
    isSearching,
    isLoading,
    loadItems,
    loadMoreItems,
    reset,
    changeEntity,
    info_types,
  };
}
